import type { TextLayer } from '../template';
import { emph, lin, money, rad, solid, step, t, tj, type Box, type RelKey, type Tx } from './kit';
import { SN, type VideoTemplateDef } from './types';

/** Beauty product: soft focus pulls, arched frames, slow crossfades. */
const beautySoft: VideoTemplateDef = {
  id: 'beauty-soft-glow',
  category: 'beauty',
  format: 'portrait',
  name: t(
    'Soft glow',
    'Ніжне сяйво',
    'Нежное сияние',
    'Delikatny blask',
    'Strălucire delicată',
    'Brillo suave',
  ),
  description: t(
    'Soft focus pulls, arched frames and slow crossfades',
    'М’які розфокуси, арочні рамки й повільні переходи',
    'Мягкие расфокусы, арочные рамки и медленные переходы',
    'Miękka ostrość, łukowe ramki i powolne przejścia',
    'Focus moale, rame arcuite și tranziții lente',
    'Desenfoques suaves, marcos en arco y fundidos lentos',
  ),
  scenes: [
    {
      key: 'intro',
      name: SN.intro,
      duration: 3200,
      transition: { preset: 'crossfade', duration: 900 },
    },
    {
      key: 'product',
      name: SN.product,
      duration: 3200,
      transition: { preset: 'blur', duration: 800 },
    },
    {
      key: 'benefits',
      name: SN.benefits,
      duration: 3200,
      transition: { preset: 'fade-through', duration: 800 },
    },
    {
      key: 'offer',
      name: SN.offer,
      duration: 2800,
      transition: { preset: 'crossfade', duration: 800 },
    },
    { key: 'cta', name: SN.cta, duration: 2800 },
  ],
  build(b) {
    const C = {
      blush: '#F6E3DF',
      rose: '#C97C7C',
      cream: '#FFF8F4',
      plum: '#3D2030',
      gold: '#C8A27A',
      white: '#FFFFFF',
    };
    const F = b.F;
    const serif: Partial<TextLayer> = {
      fontFamily: 'Cormorant Garamond',
      fontWeight: 600,
      fontStyle: 'italic',
      lineHeight: 1,
    };
    const caps = (size: number, color: string): Partial<TextLayer> => ({
      fontFamily: 'Raleway',
      fontWeight: 600,
      fontSize: size,
      color,
      textTransform: 'uppercase',
      letterSpacing: Math.round(size * 0.28),
    });

    const s0 = b.sc('intro');
    b.photo(s0, 'photo_intro', 'beauty-portrait', b.full(), {}, { keys: b.kenBurns(1.14, 1.02) });
    b.shade(s0, 'shade_intro', b.full(), [
      [0, '#3D203000'],
      [0.5, '#3D203000'],
      [1, '#3D2030B3'],
    ]);
    b.text(
      s0,
      'kicker',
      t(
        'New · Silk Lip Serum',
        'Новинка · Silk Lip Serum',
        'Новинка · Silk Lip Serum',
        'Nowość · Silk Lip Serum',
        'Nou · Silk Lip Serum',
        'Novedad · Silk Lip Serum',
      ),
      b.in(0, 0.68, 1, 0.06),
      caps(b.s(0.028), C.cream),
      { enter: step('fade', 700, 800) },
    );
    b.text(
      s0,
      'headline',
      t(
        'Glow, naturally',
        'Сяй природно',
        'Сияй естественно',
        'Blask w naturze',
        'Strălucește natural',
        'Brilla al natural',
      ),
      b.in(0, 0.75, 1, 0.2),
      { ...serif, fontSize: b.s(0.15), color: C.white },
      {
        enter: step('letters-blur', 900, 900),
        split: { by: 'char', stagger: 40, order: 'center' },
      },
    );
    b.addCamera(s0, 'focus', 0, 1400, { intensity: 0.8 });

    const s1 = b.sc('product');
    b.backdrop(s1, 'product_bg', lin(160, C.cream, C.blush));
    const arch = b.pick<Box>({
      tall: b.in(0.2, 0.02, 0.6, 0.62),
      portrait: b.in(0.26, 0.02, 0.48, 0.66),
      square: b.in(0.06, 0.04, 0.4, 0.92),
      wide: b.in(0.1, 0.02, 0.28, 0.96),
    });
    const off = b.s(0.025);
    b.shape(
      s1,
      'arch_outline',
      { x: arch.x + off, y: arch.y + off, w: arch.w, h: arch.h - off },
      {
        fill: null,
        strokeWidth: Math.max(2, b.s(0.003)),
        strokeColor: C.rose,
        borderRadius: arch.w / 2,
      },
      { enter: step('fade', 500, 1000) },
    );
    const product = b.photo(
      s1,
      'product_photo',
      'lipstick',
      { ...arch, h: arch.h - off },
      { borderRadius: arch.w / 2, focusX: 0.55 },
      { enter: step('blur-in', 0, 1000), keys: b.kenBurns(1, 1.06) },
    );
    b.addFx(s1, 'sweep', 1200, 1200, { target: product.id, opacity: 0.4 });
    const pt = b.upright
      ? b.in(0, 0.7, 1, 0.3)
      : b.in(b.wide ? 0.46 : 0.5, 0.22, b.wide ? 0.54 : 0.5, 0.56);
    const al = b.upright ? 'center' : 'left';
    b.text(
      s1,
      'product_name',
      'Silk Lip Serum',
      { x: pt.x, y: pt.y, w: pt.w, h: pt.h * 0.5 },
      { ...serif, fontSize: b.s(0.11), color: C.plum, align: al },
      { enter: step('lines-mask', 600, 900), split: { by: 'line', stagger: 150 } },
    );
    b.text(
      s1,
      'product_line',
      tj(
        t(
          'Tinted care · ',
          'Тонувальний догляд · ',
          'Тонирующий уход · ',
          'Koloryzująca pielęgnacja · ',
          'Îngrijire nuanțată · ',
          'Cuidado con color · ',
        ),
        money({ usd: 28, uah: 990, rub: 1990, pln: 99, ron: 119, eur: 26 }),
      ),
      { x: pt.x, y: pt.y + pt.h * 0.56, w: pt.w, h: pt.h * 0.18 },
      caps(b.s(0.028), C.rose),
      { enter: step('fade', 1200, 700) },
    );

    const s2 = b.sc('benefits');
    b.backdrop(s2, 'benefits_bg', solid(C.cream));
    const side = b.pick<Box>({
      tall: b.in(0, 0, 1, 0.44),
      portrait: b.in(0, 0, 1, 0.44),
      square: b.in(0, 0, 0.42, 1),
      wide: b.in(0, 0, 0.4, 1),
    });
    b.photo(
      s2,
      'benefits_photo',
      'glam-portrait',
      side,
      { borderRadius: b.s(0.03) },
      { enter: step('fade', 0, 900), keys: b.kenBurns(1.04, 1.12, 0, -0.01) },
    );
    const list = b.upright
      ? b.in(0.04, 0.5, 0.92, 0.5)
      : b.in(b.wide ? 0.46 : 0.48, 0.08, b.wide ? 0.54 : 0.52, 0.84);
    const benefits: Tx[] = [
      t(
        'Hyaluronic care',
        'Гіалуроновий догляд',
        'Гиалуроновый уход',
        'Pielęgnacja z hialuronem',
        'Îngrijire cu acid hialuronic',
        'Cuidado con hialurónico',
      ),
      t(
        '8-hour hydration',
        'Зволоження на 8 годин',
        'Увлажнение на 8 часов',
        'Nawilżenie na 8 godzin',
        'Hidratare 8 ore',
        'Hidratación 8 horas',
      ),
      t(
        'Vegan formula',
        'Веганська формула',
        'Веганская формула',
        'Wegańska formuła',
        'Formulă vegană',
        'Fórmula vegana',
      ),
    ];
    const rh = list.h / 3;
    benefits.forEach((s, i) => {
      const y = list.y + i * rh;
      const sp = Math.min(rh * 0.4, b.s(0.06));
      b.shape(
        s2,
        `sparkle_${i + 1}`,
        { x: list.x, y: y + (rh - sp) / 2, w: sp, h: sp },
        { shape: 'star', points: 4, innerRatio: 0.3, fill: solid(C.gold) },
        {
          enter: step('spin', 500 + i * 300, 700, 'out-back'),
          emphasis: emph('pulse', 1300 + i * 200, 1600, 0),
        },
      );
      b.text(
        s2,
        `benefit_${i + 1}`,
        s,
        { x: list.x + sp * 1.6, y, w: list.w - sp * 1.6, h: rh },
        { ...serif, fontSize: b.s(0.075), color: C.plum, align: 'left' },
        {
          enter: step('drift', 550 + i * 300, 900, 'out-quart', {
            direction: 'right',
            distance: 8,
          }),
        },
      );
    });

    const s3 = b.sc('offer');
    b.photo(
      s3,
      'photo_offer',
      'glam-portrait',
      b.full(),
      { brightness: 0.75, blur: 3 },
      { keys: b.kenBurns(1.08, 1.14) },
    );
    b.shape(s3, 'offer_tint', b.full(), { fill: solid('#3D203066') });
    const card = b.centered(
      b.cx,
      b.cy,
      Math.min(F.w * 0.8, b.s(0.7)),
      Math.min(F.h * 0.6, b.s(0.6)),
    );
    b.shape(
      s3,
      'offer_card',
      card,
      { fill: solid('#FFF8F4F2'), borderRadius: b.s(0.03) },
      { enter: step('zoom-in', 0, 900, 'out-quart', { intensity: 0.3 }) },
    );
    b.text(
      s3,
      'offer_kicker',
      t(
        'Only this week',
        'Лише цього тижня',
        'Только на этой неделе',
        'Tylko w tym tygodniu',
        'Doar săptămâna asta',
        'Solo esta semana',
      ),
      { x: card.x, y: card.y + card.h * 0.12, w: card.w, h: card.h * 0.12 },
      caps(b.s(0.026), C.rose),
      { enter: step('fade', 400, 600) },
    );
    b.text(
      s3,
      'discount',
      '−20%',
      { x: card.x, y: card.y + card.h * 0.26, w: card.w, h: card.h * 0.4 },
      { ...serif, fontSize: b.s(0.22), color: C.plum },
      { enter: step('blur-in', 500, 800), emphasis: emph('pulse', 1400, 1400, 1) },
    );
    b.chip(
      s3,
      'promo_code',
      t('code GLOW', 'код GLOW', 'код GLOW', 'kod GLOW', 'cod GLOW', 'código GLOW'),
      { x: b.cx, y: card.y + card.h * 0.72, h: b.s(0.075), align: 'center' },
      {
        bg: C.plum,
        color: C.cream,
        font: 'Raleway',
        weight: 600,
        upper: true,
        extra: { letterSpacing: b.s(0.006) },
      },
      { enter: step('fade', 1000, 600) },
    );

    const s4 = b.sc('cta');
    b.backdrop(s4, 'cta_bg', lin(180, C.blush, C.cream));
    b.shape(
      s4,
      'cta_rule',
      b.centered(b.cx, F.y + F.h * 0.26, b.s(0.12), Math.max(2, b.s(0.003))),
      { fill: solid(C.gold) },
      { enter: step('unfold', 200, 700, 'out-expo', { direction: 'right' }) },
    );
    b.text(
      s4,
      'cta_headline',
      t(
        'Discover the collection',
        'Відкрийте колекцію',
        'Откройте коллекцию',
        'Odkryj kolekcję',
        'Descoperă colecția',
        'Descubre la colección',
      ),
      b.in(0, 0.3, 1, 0.26),
      { ...serif, fontSize: b.s(0.12), color: C.plum },
      { enter: step('lines-mask', 300, 900), split: { by: 'line', stagger: 150 } },
    );
    b.chip(
      s4,
      'cta',
      t('Shop now', 'Купити', 'Купить', 'Kup teraz', 'Cumpără', 'Comprar'),
      { x: b.cx, y: F.y + F.h * 0.62, h: b.s(0.1), align: 'center' },
      {
        bg: C.plum,
        color: C.cream,
        font: 'Raleway',
        weight: 600,
        upper: true,
        extra: { letterSpacing: b.s(0.008) },
      },
      { enter: step('fade', 1000, 700) },
    );
    b.text(s4, 'site', 'maisonglow.com', b.in(0, 0.8, 1, 0.07), caps(b.s(0.026), C.rose), {
      enter: step('fade', 1300, 600),
    });
    return solid(C.cream);
  },
};

/** Car dealer offer: diagonal wipes, speed lines, spec chips and finance offer. */
const autoOffer: VideoTemplateDef = {
  id: 'auto-dealer-offer',
  category: 'auto',
  format: 'wide',
  name: t(
    'Dealer offer',
    'Пропозиція автосалону',
    'Предложение автосалона',
    'Oferta salonu',
    'Ofertă dealer auto',
    'Oferta del concesionario',
  ),
  description: t(
    'Diagonal wipes, speed lines, spec chips and a finance offer',
    'Діагональні шторки, лінії швидкості, характеристики й кредит',
    'Диагональные шторки, линии скорости, характеристики и кредит',
    'Ukośne kurtyny, linie prędkości, parametry i finansowanie',
    'Cortine diagonale, linii de viteză, specificații și finanțare',
    'Barridos diagonales, líneas de velocidad, ficha y financiación',
  ),
  scenes: [
    {
      key: 'hook',
      name: SN.hook,
      duration: 2400,
      transition: {
        preset: 'diagonal',
        duration: 800,
        params: { color: '#E10600', color2: '#111317', color3: '#FFFFFF' },
      },
    },
    {
      key: 'car',
      name: t('Model', 'Модель', 'Модель', 'Model', 'Model', 'Modelo'),
      duration: 3200,
      transition: { preset: 'whip-pan', duration: 450, params: { direction: 'right' } },
    },
    {
      key: 'offer',
      name: SN.offer,
      duration: 3000,
      transition: { preset: 'push', duration: 500, params: { direction: 'left' } },
    },
    {
      key: 'tradein',
      name: t('Trade-in', 'Trade-in', 'Трейд-ин', 'Trade-in', 'Trade-in', 'Entrega'),
      duration: 2800,
      transition: { preset: 'zoom-in', duration: 500 },
    },
    { key: 'cta', name: SN.cta, duration: 2800 },
  ],
  build(b) {
    const C = {
      asphalt: '#111317',
      red: '#E10600',
      white: '#FFFFFF',
      silver: '#C9CED6',
      panel: '#1C1F25',
    };
    const F = b.F;
    const head: Partial<TextLayer> = {
      fontFamily: 'Exo 2',
      fontWeight: 800,
      fontStyle: 'italic',
      textTransform: 'uppercase',
      lineHeight: 0.95,
    };
    const body: Partial<TextLayer> = { fontFamily: 'Inter', fontWeight: 600 };

    const s0 = b.sc('hook');
    b.photo(
      s0,
      'photo_hook',
      'yellow-car',
      b.full(),
      { brightness: 0.7, focusY: 0.6 },
      { keys: b.punchIn(1.25, 1.02, 1.08, 400) },
    );
    b.shade(s0, 'shade_hook', b.full(), [
      [0, '#11131700'],
      [0.45, '#11131733'],
      [1, '#111317E6'],
    ]);
    const band = b.upright ? b.in(0, 0.56, 1, 0.3) : b.in(0, 0.5, 0.75, 0.36);
    b.shape(
      s0,
      'hook_band',
      { x: 0, y: band.y, w: band.x + band.w + b.s(0.04), h: band.h },
      { shape: 'parallelogram', fill: solid(C.red) },
      {
        keys: [
          { at: 0, x: -Math.round(b.W), easing: 'out-expo' },
          { at: 450, x: 0 },
        ],
      },
    );
    b.text(
      s0,
      'headline',
      t(
        'DRIVE IT HOME TODAY',
        'ЗАБИРАЙ СЬОГОДНІ',
        'ЗАБИРАЙ СЕГОДНЯ',
        'ODBIERZ JUŻ DZIŚ',
        'PLEACĂ CU EA AZI',
        'LLÉVATELO HOY',
      ),
      { x: band.x, y: band.y + band.h * 0.08, w: band.w * 0.92, h: band.h * 0.84 },
      { ...head, fontSize: b.s(0.12), color: C.white, align: 'left' },
      { enter: step('slide', 300, 450, 'out-expo', { direction: 'right', distance: 40 }) },
    );
    b.addFx(s0, 'lines', 0, 900, {
      direction: 'right',
      color: '#FFFFFF',
      color2: C.red,
      opacity: 0.6,
      count: 20,
    });
    b.addCamera(s0, 'zoom-burst', 0, 700, { intensity: 0.8 });

    const s1 = b.sc('car');
    b.photo(
      s1,
      'photo_car',
      'suv',
      b.full(),
      { focusX: 0.66 },
      { keys: b.kenBurns(1.12, 1.04, -0.03) },
    );
    b.shade(s1, 'shade_car', b.full(), [
      [0, '#11131700'],
      [0.5, '#11131700'],
      [1, '#111317F2'],
    ]);
    const mt = b.upright
      ? b.in(0, 0.5, 1, 0.2)
      : b.wide
        ? b.in(0, 0.56, 0.6, 0.18)
        : b.in(0, 0.4, 1, 0.17);
    b.text(
      s1,
      'model',
      'Terra X 2026',
      mt,
      { ...head, fontSize: b.s(0.11), color: C.white, align: 'left' },
      { enter: step('slide', 100, 500, 'out-expo', { direction: 'right', distance: 30 }) },
    );
    const specs: Array<{ icon: string; text: Tx }> = [
      { icon: 'zap', text: t('245 hp', '245 к.с.', '245 л.с.', '245 KM', '245 CP', '245 CV') },
      {
        icon: 'shield-check',
        text: t(
          '5★ safety',
          '5★ безпека',
          '5★ безопасность',
          '5★ bezpieczeństwo',
          '5★ siguranță',
          '5★ seguridad',
        ),
      },
      {
        icon: 'car',
        text: t(
          'All-wheel drive',
          'Повний привід',
          'Полный привод',
          'Napęd 4x4',
          'Tracțiune integrală',
          'Tracción total',
        ),
      },
    ];
    const sh = b.s(0.085);
    const sy = mt.y + mt.h + b.s(0.03);
    const col = !b.wide;
    const sw = col ? F.w : (F.w - b.s(0.04)) / 3;
    specs.forEach((sp, i) => {
      const x = col ? F.x : F.x + i * (sw + b.s(0.02));
      const y = col ? sy + i * (sh + b.s(0.02)) : sy;
      const w = col ? Math.min(F.w, b.s(0.6)) : sw;
      b.shape(
        s1,
        `spec_bg_${i + 1}`,
        { x, y, w, h: sh },
        {
          fill: solid('#111317CC'),
          borderRadius: b.s(0.012),
          strokeWidth: Math.max(2, b.s(0.003)),
          strokeColor: C.red,
        },
        {
          enter: step('slide', 500 + i * 160, 450, 'out-expo', {
            direction: 'right',
            distance: 25,
          }),
        },
      );
      b.icon(
        s1,
        `spec_icon_${i + 1}`,
        sp.icon,
        { x: x + sh * 0.25, y: y + sh * 0.22, w: sh * 0.56, h: sh * 0.56 },
        C.red,
        {
          enter: step('pop', 650 + i * 160, 400, 'out-back'),
        },
      );
      b.text(
        s1,
        `spec_${i + 1}`,
        sp.text,
        { x: x + sh, y, w: w - sh * 1.15, h: sh },
        {
          ...body,
          fontWeight: 700,
          fontSize: b.s(0.04),
          color: C.white,
          align: 'left',
          maxLines: 1,
        },
        { enter: step('fade', 650 + i * 160, 400) },
      );
    });

    const s2 = b.sc('offer');
    b.backdrop(s2, 'offer_bg', rad('#262A33', C.asphalt));
    b.photo(
      s2,
      'offer_photo',
      'suv',
      b.full(),
      { focusX: 0.66, grayscale: true, opacity: 0.16 },
      {
        keys: b.kenBurns(1.04, 1.12, 0.02),
      },
    );
    b.shape(
      s2,
      'offer_stripe',
      { x: 0, y: F.y + F.h * 0.65, w: b.W, h: b.s(0.05) },
      { shape: 'parallelogram', fill: solid(C.red) },
      { enter: step('wipe', 0, 450, 'out-expo', { direction: 'right' }) },
    );
    b.text(
      s2,
      'offer_kicker',
      t(
        'Finance offer',
        'Кредитна пропозиція',
        'Кредитное предложение',
        'Oferta finansowania',
        'Ofertă de finanțare',
        'Oferta de financiación',
      ),
      b.in(0, 0.08, 1, 0.08),
      {
        ...body,
        fontSize: b.s(0.034),
        color: C.silver,
        textTransform: 'uppercase',
        letterSpacing: b.s(0.008),
      },
      { enter: step('fade', 100, 400) },
    );
    b.text(
      s2,
      'rate',
      t('0% APR', '0% кредит', '0% кредит', '0% RRSO', '0% dobândă', '0% TIN'),
      b.in(0, 0.18, 1, 0.3),
      { ...head, fontSize: b.s(0.22), color: C.white },
      { enter: step('zoom-out', 200, 500, 'out-expo') },
    );
    b.text(
      s2,
      'price',
      tj(
        t('from ', 'від ', 'от ', 'od ', 'de la ', 'desde '),
        money({ usd: 299, uah: 12900, rub: 29900, pln: 1290, ron: 1490, eur: 279 }),
        t('/mo', '/міс', '/мес', '/mies.', '/lună', '/mes'),
      ),
      b.in(0, 0.48, 1, 0.13),
      { ...head, fontSize: b.s(0.09), color: C.red, maxLines: 1 },
      {
        enter: step('slide', 600, 450, 'out-expo', { direction: 'left', distance: 30 }),
        emphasis: emph('pulse', 1300, 1400, 0),
      },
    );
    b.text(
      s2,
      'disclaimer',
      t(
        '36 months, subject to approval',
        '36 місяців, за умови схвалення',
        '36 месяцев, при одобрении банка',
        '36 miesięcy, po akceptacji banku',
        '36 de luni, sub rezerva aprobării',
        '36 meses, sujeto a aprobación',
      ),
      b.in(0, 0.78, 1, 0.07),
      { ...body, fontWeight: 500, fontSize: b.s(0.03), color: C.silver },
      { enter: step('fade', 1100, 400) },
    );

    const s3 = b.sc('tradein');
    b.backdrop(s3, 'tradein_bg', solid(C.panel));
    const pic: Box = b.upright
      ? { x: 0, y: 0, w: b.W, h: b.tall ? F.y + F.h * 0.5 : b.H * 0.5 }
      : { x: b.W * 0.5, y: 0, w: b.W * 0.5, h: b.H };
    b.photo(s3, 'photo_tradein', 'taxi', pic, {}, { keys: b.kenBurns(1.04, 1.12, 0.02) });
    const tt = b.upright
      ? b.in(0, b.tall ? 0.56 : 0.56, 1, b.tall ? 0.44 : 0.44)
      : b.in(0, 0.16, 0.44, 0.68);
    b.text(
      s3,
      'tradein_title',
      t(
        'Trade-in bonus',
        'Бонус за трейд-ін',
        'Бонус за трейд-ин',
        'Bonus za stare auto',
        'Bonus la schimb',
        'Bonus por tu coche',
      ),
      { x: tt.x, y: tt.y, w: tt.w, h: tt.h * 0.28 },
      { ...head, fontSize: b.s(0.075), color: C.white, align: 'left' },
      { enter: step('slide', 0, 450, 'out-expo', { direction: 'right', distance: 25 }) },
    );
    b.text(
      s3,
      'tradein_amount',
      tj(
        t('up to ', 'до ', 'до ', 'do ', 'până la ', 'hasta '),
        money({ usd: 3000, uah: 120000, rub: 270000, pln: 12000, ron: 15000, eur: 2800 }),
      ),
      { x: tt.x, y: tt.y + tt.h * 0.3, w: tt.w, h: tt.h * 0.3 },
      { ...head, fontSize: b.s(0.12), color: C.red, align: 'left', maxLines: 1 },
      { enter: step('pop', 350, 450, 'spring-bouncy'), emphasis: emph('heartbeat', 1200, 900) },
    );
    b.text(
      s3,
      'tradein_note',
      t(
        'Bring your car — we value it in 30 minutes',
        'Приїжджайте — оцінимо авто за 30 хвилин',
        'Приезжайте — оценим авто за 30 минут',
        'Przyjedź — wycenimy auto w 30 minut',
        'Vino cu mașina — o evaluăm în 30 de minute',
        'Trae tu coche y lo tasamos en 30 minutos',
      ),
      { x: tt.x, y: tt.y + tt.h * 0.64, w: tt.w, h: tt.h * 0.3 },
      { ...body, fontSize: b.s(0.038), color: C.silver, align: 'left', lineHeight: 1.3 },
      { enter: step('fade', 700, 450) },
    );

    const s4 = b.sc('cta');
    b.photo(
      s4,
      'photo_cta',
      'suv',
      b.full(),
      { brightness: 0.45, focusX: 0.7 },
      { keys: b.kenBurns(1.04, 1.1) },
    );
    b.shape(s4, 'cta_tint', b.full(), { fill: lin(90, '#111317F2', '#11131780') });
    b.text(
      s4,
      'cta_headline',
      t(
        'Book a test drive',
        'Запишіться на тест-драйв',
        'Запишитесь на тест-драйв',
        'Umów jazdę próbną',
        'Programează un test drive',
        'Reserva tu prueba',
      ),
      b.in(0, 0.2, 1, 0.3),
      { ...head, fontSize: b.s(0.12), color: C.white },
      { enter: step('slide', 0, 500, 'out-expo', { direction: 'right', distance: 30 }) },
    );
    b.chip(
      s4,
      'cta',
      t('Book now', 'Записатися', 'Записаться', 'Zarezerwuj', 'Rezervă', 'Reservar'),
      { x: b.cx, y: F.y + F.h * 0.56, h: b.s(0.11), align: 'center' },
      {
        bg: C.red,
        color: C.white,
        font: 'Exo 2',
        weight: 800,
        upper: true,
        radius: b.s(0.012),
        extra: { fontStyle: 'italic' },
      },
      { enter: step('pop', 500, 450, 'out-back'), emphasis: emph('shine', 1200, 900, 0, 500) },
    );
    const pin = b.s(0.05);
    const addr = t(
      'Motor Plaza, 25 Ring Road',
      'Motor Plaza, Кільцева, 25',
      'Motor Plaza, Кольцевая, 25',
      'Motor Plaza, ul. Obwodowa 25',
      'Motor Plaza, Șos. Centurii 25',
      'Motor Plaza, Ronda 25',
    );
    const aw = Math.min(F.w - pin * 1.3, b.textW(addr, b.s(0.036)) + b.s(0.02));
    const ax = b.cx - (aw + pin * 1.3) / 2;
    const ay = F.y + F.h * 0.76;
    b.icon(s4, 'address_icon', 'map-pin', { x: ax, y: ay, w: pin, h: pin }, C.red, {
      enter: step('drop', 900, 500, 'bounce'),
    });
    b.text(
      s4,
      'address',
      addr,
      { x: ax + pin * 1.3, y: ay, w: aw, h: pin },
      { ...body, fontSize: b.s(0.036), color: C.white, align: 'left', maxLines: 1 },
      { enter: step('fade', 1000, 400) },
    );
    return solid(C.asphalt);
  },
};

/** "We're hiring": bold type, role cards and perks. */
const hiring: VideoTemplateDef = {
  id: 'hiring-roles',
  category: 'hiring',
  format: 'square',
  name: t("We're hiring", 'Ми наймаємо', 'Мы нанимаем', 'Rekrutujemy', 'Angajăm', 'Contratamos'),
  description: t(
    'Bold type, stacked role cards and pop-in perks',
    'Жирний шрифт, картки вакансій і переваги, що вискакують',
    'Жирный шрифт, карточки вакансий и всплывающие преимущества',
    'Mocna typografia, karty stanowisk i wyskakujące benefity',
    'Tipografie îndrăzneață, carduri de posturi și beneficii',
    'Tipografía rotunda, tarjetas de puestos y ventajas',
  ),
  scenes: [
    {
      key: 'hook',
      name: SN.hook,
      duration: 2400,
      transition: { preset: 'stack', duration: 600, params: { direction: 'up' } },
    },
    {
      key: 'team',
      name: t('Team', 'Команда', 'Команда', 'Zespół', 'Echipa', 'Equipo'),
      duration: 2800,
      transition: { preset: 'push', duration: 500, params: { direction: 'left' } },
    },
    {
      key: 'roles',
      name: t('Roles', 'Вакансії', 'Вакансии', 'Stanowiska', 'Posturi', 'Puestos'),
      duration: 3800,
      transition: {
        preset: 'circle',
        duration: 900,
        params: { color: '#2F6BFF', color2: '#FFD43B', color3: '#121212' },
      },
    },
    {
      key: 'perks',
      name: SN.benefits,
      duration: 3000,
      transition: { preset: 'slide-over', duration: 600, params: { direction: 'up' } },
    },
    { key: 'cta', name: SN.cta, duration: 2600 },
  ],
  build(b) {
    const C = {
      yellow: '#FFD43B',
      ink: '#121212',
      white: '#FFFFFF',
      blue: '#2F6BFF',
      paper: '#F7F5F0',
    };
    const F = b.F;
    const bold: Partial<TextLayer> = { fontFamily: 'Rubik', fontWeight: 800, lineHeight: 1 };
    const body: Partial<TextLayer> = { fontFamily: 'Rubik', fontWeight: 500 };

    const s0 = b.sc('hook');
    b.backdrop(s0, 'hook_bg', solid(C.yellow));
    const mg = b.s(0.2);
    b.icon(
      s0,
      'megaphone',
      'megaphone',
      b.centered(b.cx, F.y + F.h * (b.tall ? 0.18 : 0.16), mg, mg),
      C.ink,
      {
        enter: step('pop', 0, 500, 'spring-bouncy'),
        emphasis: emph('wiggle', 700, 700, 2, 300),
      },
    );
    b.text(
      s0,
      'headline',
      t("WE'RE HIRING", 'МИ НАЙМАЄМО', 'МЫ НАНИМАЕМ', 'REKRUTUJEMY', 'ANGAJĂM', 'CONTRATAMOS'),
      b.in(0, b.tall ? 0.34 : 0.32, 1, 0.36),
      { ...bold, fontWeight: 900, fontSize: b.s(0.18), color: C.ink, textTransform: 'uppercase' },
      { enter: step('words-flip', 200, 600), split: { by: 'word', stagger: 120 } },
    );
    b.shape(
      s0,
      'headline_bar',
      b.centered(b.cx, F.y + F.h * 0.76, Math.min(F.w * 0.6, b.s(0.5)), b.s(0.03)),
      { fill: solid(C.ink), borderRadius: b.s(0.03) },
      { enter: step('wipe', 800, 500, 'out-expo', { direction: 'right' }) },
    );
    [
      { x: 0.04, y: 0.06, r: -12 },
      { x: 0.86, y: 0.82, r: 15 },
    ].forEach((p, i) =>
      b.shape(
        s0,
        `sparkle_${i + 1}`,
        { ...b.in(p.x, p.y, 0, 0), w: b.s(0.1), h: b.s(0.1) },
        { shape: 'icon', icon: 'sparkles', fill: solid(C.ink), rotation: p.r },
        {
          enter: step('pop', 500 + i * 150, 400, 'out-back'),
          emphasis: emph('pulse', 1000, 1600, 0),
        },
      ),
    );

    const s1 = b.sc('team');
    b.backdrop(s1, 'team_bg', solid(C.paper));
    const card = b.pick<Box>({
      tall: b.in(0, 0, 1, 0.52),
      portrait: b.in(0, 0, 1, 0.52),
      square: b.in(0, 0, 0.5, 1),
      wide: b.in(0, 0, 0.5, 1),
    });
    b.photo(
      s1,
      'team_photo',
      'remote-work',
      card,
      { borderRadius: b.s(0.04) },
      { enter: step('pop', 0, 550, 'out-back'), keys: b.kenBurns(1, 1.08) },
    );
    const st = b.s(0.2);
    b.shape(
      s1,
      'team_sticker',
      { x: card.x + card.w - st * 0.9, y: card.y + card.h - st * 0.9, w: st, h: st },
      { shape: 'star', points: 12, innerRatio: 0.8, fill: solid(C.yellow) },
      { enter: step('spin', 600, 600, 'out-back'), emphasis: emph('spin', 1300, 8000, 1) },
    );
    b.text(
      s1,
      'team_size',
      '50+',
      {
        x: card.x + card.w - st * 0.9 + st * 0.15,
        y: card.y + card.h - st * 0.9 + st * 0.15,
        w: st * 0.7,
        h: st * 0.7,
      },
      { ...bold, fontWeight: 900, fontSize: b.s(0.06), color: C.ink, rotation: -10 },
      { enter: step('pop', 700, 450, 'out-back') },
    );
    const tt = b.upright ? b.in(0, 0.58, 1, 0.42) : b.in(0.55, 0.16, 0.45, 0.68);
    const al = b.upright ? 'center' : 'left';
    b.text(
      s1,
      'team_headline',
      t(
        'Join a team that ships',
        'Приєднуйся до команди, що запускає',
        'Присоединяйся к команде, которая запускает',
        'Dołącz do zespołu, który dowozi',
        'Alătură-te unei echipe care livrează',
        'Únete a un equipo que lanza',
      ),
      { x: tt.x, y: tt.y, w: tt.w, h: tt.h * 0.6 },
      { ...bold, fontSize: b.s(0.085), color: C.ink, align: al, lineHeight: 1.05 },
      { enter: step('words-rise', 250, 500), split: { by: 'word', stagger: 70 } },
    );
    b.text(
      s1,
      'team_sub',
      t(
        'Remote-first product studio',
        'Продуктова студія, дистанційно',
        'Продуктовая студия, удалённо',
        'Studio produktowe, zdalnie',
        'Studio de produs, remote',
        'Estudio de producto en remoto',
      ),
      { x: tt.x, y: tt.y + tt.h * 0.66, w: tt.w, h: tt.h * 0.2 },
      { ...body, fontSize: b.s(0.042), color: '#55534E', align: al },
      { enter: step('fade', 800, 400) },
    );

    const s2 = b.sc('roles');
    b.backdrop(s2, 'roles_bg', solid(C.paper));
    b.text(
      s2,
      'roles_title',
      t(
        'Open roles',
        'Відкриті вакансії',
        'Открытые вакансии',
        'Otwarte stanowiska',
        'Posturi deschise',
        'Puestos abiertos',
      ),
      b.in(0, 0, 1, 0.16),
      { ...bold, fontSize: b.s(0.09), color: C.ink, align: 'left' },
      { enter: step('slide', 0, 450, 'out-expo', { direction: 'right', distance: 20 }) },
    );
    const roles: Array<{ role: Tx; tag: Tx }> = [
      {
        role: t(
          'Product designer',
          'Продуктовий дизайнер',
          'Продуктовый дизайнер',
          'Product designer',
          'Designer de produs',
          'Diseñador de producto',
        ),
        tag: t('Remote', 'Віддалено', 'Удалённо', 'Zdalnie', 'Remote', 'Remoto'),
      },
      {
        role: t(
          'Frontend developer',
          'Frontend-розробник',
          'Frontend-разработчик',
          'Frontend developer',
          'Dezvoltator frontend',
          'Desarrollador frontend',
        ),
        tag: t(
          'Full-time',
          'Повний день',
          'Полный день',
          'Pełny etat',
          'Normă întreagă',
          'Jornada completa',
        ),
      },
      {
        role: t(
          'Sales manager',
          'Менеджер з продажу',
          'Менеджер по продажам',
          'Sales manager',
          'Manager de vânzări',
          'Responsable de ventas',
        ),
        tag: t('Hybrid', 'Гібрид', 'Гибрид', 'Hybrydowo', 'Hibrid', 'Híbrido'),
      },
    ];
    const area = b.in(0, 0.2, 1, 0.8);
    const gap = b.s(0.025);
    const rh = Math.min((area.h - gap * 2) / 3, b.s(0.22));
    roles.forEach((r, i) => {
      const y = area.y + i * (rh + gap);
      b.shape(
        s2,
        `role_card_${i + 1}`,
        { x: area.x, y, w: area.w, h: rh },
        {
          fill: solid(i === 0 ? C.yellow : C.white),
          borderRadius: b.s(0.025),
          strokeWidth: Math.max(2, b.s(0.004)),
          strokeColor: C.ink,
          shadow: { x: b.s(0.008), y: b.s(0.008), blur: 0, color: C.ink },
        },
        {
          enter: step('slide', 300 + i * 200, 500, 'out-expo', { direction: 'left', distance: 60 }),
        },
      );
      const pad = b.s(0.03);
      const arrow = rh * 0.36;
      b.text(
        s2,
        `role_${i + 1}`,
        r.role,
        { x: area.x + pad, y: y + rh * 0.12, w: area.w - pad * 2 - arrow * 1.4, h: rh * 0.46 },
        { ...bold, fontWeight: 700, fontSize: b.s(0.055), color: C.ink, align: 'left' },
        { enter: step('fade', 450 + i * 200, 400) },
      );
      b.chip(
        s2,
        `role_tag_${i + 1}`,
        r.tag,
        { x: area.x + pad, y: y + rh * 0.62, h: Math.min(rh * 0.28, b.s(0.06)) },
        { bg: C.blue, color: C.white, font: 'Rubik', weight: 600 },
        { enter: step('pop', 550 + i * 200, 350, 'out-back') },
      );
      b.icon(
        s2,
        `role_arrow_${i + 1}`,
        'arrow-up-right',
        { x: area.x + area.w - pad - arrow, y: y + (rh - arrow) / 2, w: arrow, h: arrow },
        C.ink,
        {
          enter: step('slide', 600 + i * 200, 400, 'out-back', {
            direction: 'right',
            distance: 40,
          }),
        },
      );
    });

    const s3 = b.sc('perks');
    b.backdrop(s3, 'perks_bg', solid(C.blue));
    b.text(
      s3,
      'perks_title',
      t(
        'Why you’ll love it',
        'Чому тобі сподобається',
        'Почему тебе понравится',
        'Dlaczego to polubisz',
        'De ce o să-ți placă',
        'Por qué te encantará',
      ),
      b.in(0, 0, 1, 0.16),
      { ...bold, fontSize: b.s(0.08), color: C.white },
      { enter: step('words-rise', 0, 450), split: { by: 'word', stagger: 70 } },
    );
    const perks: Array<{ icon: string; text: Tx }> = [
      {
        icon: 'house',
        text: t(
          'Work from anywhere',
          'Працюй звідусіль',
          'Работай откуда угодно',
          'Pracuj skądkolwiek',
          'Lucrezi de oriunde',
          'Trabaja desde donde quieras',
        ),
      },
      {
        icon: 'trending-up',
        text: t(
          'Learning budget',
          'Бюджет на навчання',
          'Бюджет на обучение',
          'Budżet na rozwój',
          'Buget de învățare',
          'Presupuesto de formación',
        ),
      },
      {
        icon: 'gift',
        text: t(
          'Yearly bonus',
          'Річний бонус',
          'Годовой бонус',
          'Roczna premia',
          'Bonus anual',
          'Bonus anual',
        ),
      },
    ];
    const pa = b.in(0, 0.22, 1, 0.78);
    perks.forEach((p, i) => {
      const cell: Box = b.upright
        ? { x: pa.x, y: pa.y + (i * pa.h) / 3, w: pa.w, h: pa.h / 3 }
        : { x: pa.x + (i * pa.w) / 3, y: pa.y, w: pa.w / 3, h: pa.h };
      const d = Math.min(b.upright ? cell.h * 0.7 : cell.w * 0.5, b.s(0.2));
      const c = b.upright
        ? { x: cell.x + d / 2, y: cell.y + cell.h / 2 }
        : { x: cell.x + cell.w / 2, y: cell.y + cell.h * 0.32 };
      b.shape(
        s3,
        `perk_circle_${i + 1}`,
        b.centered(c.x, c.y, d, d),
        { shape: 'ellipse', fill: solid(C.white) },
        {
          enter: step('pop', 300 + i * 200, 450, 'spring-bouncy'),
        },
      );
      b.icon(s3, `perk_icon_${i + 1}`, p.icon, b.centered(c.x, c.y, d * 0.5, d * 0.5), C.blue, {
        enter: step('pop', 380 + i * 200, 450, 'spring-bouncy'),
      });
      b.text(
        s3,
        `perk_${i + 1}`,
        p.text,
        b.upright
          ? { x: cell.x + d * 1.25, y: cell.y, w: cell.w - d * 1.25, h: cell.h }
          : {
              x: cell.x + b.s(0.01),
              y: cell.y + cell.h * 0.32 + d / 2 + b.s(0.03),
              w: cell.w - b.s(0.02),
              h: cell.h * 0.3,
            },
        {
          ...bold,
          fontWeight: 700,
          fontSize: b.s(0.055),
          color: C.white,
          align: b.upright ? 'left' : 'center',
          verticalAlign: b.upright ? 'middle' : 'top',
        },
        { enter: step('rise', 450 + i * 200, 450, 'out-expo') },
      );
    });

    const s4 = b.sc('cta');
    b.backdrop(s4, 'cta_bg', solid(C.yellow));
    const av = Math.min(b.s(0.16), F.w * 0.2);
    (['remote-work', 'fashion-selfie', 'guitarist'] as const).forEach((p, i) =>
      b.photo(
        s4,
        `team_face_${i + 1}`,
        p,
        b.centered(b.cx + (i - 1) * av * 0.8, F.y + F.h * (b.tall ? 0.12 : 0.1), av, av),
        { borderRadius: av, borderWidth: b.s(0.008), borderColor: C.ink },
        { enter: step('pop', i * 120, 450, 'spring-bouncy') },
      ),
    );
    b.text(
      s4,
      'cta_headline',
      t(
        'Apply in 2 minutes',
        'Відгукнись за 2 хвилини',
        'Откликнись за 2 минуты',
        'Aplikuj w 2 minuty',
        'Aplică în 2 minute',
        'Postúlate en 2 minutos',
      ),
      b.in(0, 0.2, 1, 0.3),
      { ...bold, fontWeight: 900, fontSize: b.s(0.12), color: C.ink },
      { enter: step('words-flip', 0, 600), split: { by: 'word', stagger: 100 } },
    );
    b.chip(
      s4,
      'cta',
      t('Apply now', 'Відгукнутися', 'Откликнуться', 'Aplikuj', 'Aplică acum', 'Postúlate'),
      { x: b.cx, y: F.y + F.h * 0.58, h: b.s(0.12), align: 'center' },
      { bg: C.ink, color: C.yellow, font: 'Rubik', weight: 800 },
      { enter: step('pop', 600, 450, 'out-back'), emphasis: emph('tada', 1300, 800, 0, 600) },
    );
    b.text(
      s4,
      'site',
      'yourcompany.com/careers',
      b.in(0, 0.78, 1, 0.08),
      { ...body, fontWeight: 600, fontSize: b.s(0.042), color: C.ink },
      { enter: step('fade', 1000, 400) },
    );
    return solid(C.yellow);
  },
};

/** Holiday sale: snowfall, iris and doors transitions, twinkling stars. */
const holiday: VideoTemplateDef = {
  id: 'holiday-gifts',
  category: 'holiday',
  format: 'square',
  name: t(
    'Holiday gifts',
    'Святкові подарунки',
    'Праздничные подарки',
    'Świąteczne prezenty',
    'Cadouri de sărbători',
    'Regalos de fiestas',
  ),
  description: t(
    'Falling snow, script titles, iris and doors transitions',
    'Сніг, рукописні заголовки, переходи «діафрагма» і «двері»',
    'Снег, рукописные заголовки, переходы «диафрагма» и «двери»',
    'Padający śnieg, odręczne napisy, przejścia przesłona i drzwi',
    'Ninsoare, titluri de mână, tranziții diafragmă și uși',
    'Nieve, títulos manuscritos, transiciones iris y puertas',
  ),
  scenes: [
    {
      key: 'greet',
      name: t('Greeting', 'Привітання', 'Поздравление', 'Życzenia', 'Urare', 'Saludo'),
      duration: 3200,
      transition: { preset: 'iris', duration: 800 },
    },
    {
      key: 'gifts',
      name: t('Gifts', 'Подарунки', 'Подарки', 'Prezenty', 'Cadouri', 'Regalos'),
      duration: 3000,
      transition: { preset: 'doors', duration: 1000 },
    },
    {
      key: 'offer',
      name: SN.offer,
      duration: 3000,
      transition: {
        preset: 'circle',
        duration: 900,
        params: { color: '#F2C14E', color2: '#0E3B2E', color3: '#D62839' },
      },
    },
    { key: 'cta', name: SN.cta, duration: 3000 },
  ],
  build(b) {
    const C = {
      green: '#0E3B2E',
      red: '#D62839',
      gold: '#F2C14E',
      cream: '#FFF6E9',
      white: '#FFFFFF',
    };
    const F = b.F;
    const script: Partial<TextLayer> = {
      fontFamily: 'Great Vibes',
      fontWeight: 400,
      lineHeight: 1.05,
    };
    const caps = (size: number, color: string): Partial<TextLayer> => ({
      fontFamily: 'Montserrat',
      fontWeight: 700,
      fontSize: size,
      color,
      textTransform: 'uppercase',
      letterSpacing: Math.round(size * 0.22),
    });

    const s0 = b.sc('greet');
    b.photo(
      s0,
      'photo_greet',
      'christmas-tree',
      b.full(),
      { brightness: 0.75 },
      { keys: b.kenBurns(1.02, 1.14) },
    );
    b.shape(s0, 'greet_tint', b.full(), { fill: rad('#0E3B2E40', '#0E3B2ED9') });
    b.text(
      s0,
      'kicker',
      t(
        'Holiday collection 2026',
        'Святкова колекція 2026',
        'Праздничная коллекция 2026',
        'Kolekcja świąteczna 2026',
        'Colecția de sărbători 2026',
        'Colección festiva 2026',
      ),
      b.in(0, 0.26, 1, 0.08),
      caps(b.s(0.03), C.gold),
      { enter: step('fade', 300, 700) },
    );
    b.text(
      s0,
      'headline',
      t(
        'Merry & Bright',
        'Щасливих свят',
        'Счастливых праздников',
        'Wesołych Świąt',
        'Sărbători fericite',
        'Felices fiestas',
      ),
      b.in(0, 0.36, 1, 0.34),
      { ...script, fontSize: b.s(0.19), color: C.cream },
      { enter: step('letters-blur', 500, 900), split: { by: 'char', stagger: 45 } },
    );
    b.addFx(s0, 'confetti', 0, 3200, {
      mode: 'rain',
      color: '#FFFFFF',
      color2: C.cream,
      color3: C.gold,
      count: 70,
    });

    const s1 = b.sc('gifts');
    b.backdrop(s1, 'gifts_bg', solid(C.cream));
    const d = b.pick({
      tall: F.w * 0.66,
      portrait: F.h * 0.42,
      square: F.h * 0.46,
      wide: F.h * 0.7,
    });
    const c = b.upright
      ? { x: b.cx, y: F.y + F.h * 0.28 }
      : { x: F.x + F.w * (b.wide ? 0.28 : 0.27), y: b.cy };
    b.shape(
      s1,
      'gift_ring',
      b.centered(c.x, c.y, d * 1.1, d * 1.1),
      {
        shape: 'ellipse',
        fill: null,
        strokeWidth: Math.max(3, b.s(0.006)),
        strokeColor: C.gold,
        strokeStyle: 'dotted',
      },
      { enter: step('zoom-in', 200, 700), emphasis: emph('spin', 900, 14000, 1) },
    );
    b.photo(
      s1,
      'gift_photo',
      'christmas-ball',
      b.centered(c.x, c.y, d, d),
      { borderRadius: d },
      {
        enter: step('pop', 0, 600, 'out-back'),
      },
    );
    const gi = b.s(0.11);
    b.shape(
      s1,
      'gift_badge',
      b.centered(c.x + d * 0.36, c.y + d * 0.36, gi * 1.5, gi * 1.5),
      { shape: 'ellipse', fill: solid(C.red) },
      { enter: step('pop', 600, 450, 'spring-bouncy') },
    );
    b.icon(
      s1,
      'gift_icon',
      'gift',
      b.centered(c.x + d * 0.36, c.y + d * 0.36, gi * 0.8, gi * 0.8),
      C.cream,
      {
        enter: step('pop', 650, 450, 'spring-bouncy'),
        emphasis: emph('wiggle', 1300, 800),
      },
    );
    const gt = b.upright
      ? b.in(0, 0.6, 1, 0.4)
      : b.in(b.wide ? 0.55 : 0.56, 0.18, b.wide ? 0.45 : 0.44, 0.64);
    const al = b.upright ? 'center' : 'left';
    b.text(
      s1,
      'gifts_headline',
      t(
        'Gifts they’ll love',
        'Подарунки, які полюблять',
        'Подарки, которые полюбят',
        'Prezenty, które pokochają',
        'Cadouri pe care le vor iubi',
        'Regalos que les encantarán',
      ),
      { x: gt.x, y: gt.y, w: gt.w, h: gt.h * 0.58 },
      { ...script, fontSize: b.s(0.13), color: C.green, align: al },
      { enter: step('fade', 400, 900) },
    );
    b.text(
      s1,
      'gifts_note',
      t(
        'Free gift wrap · delivery by Dec 23',
        'Безкоштовне пакування · доставка до 23.12',
        'Бесплатная упаковка · доставка до 23.12',
        'Darmowe pakowanie · dostawa do 23.12',
        'Ambalare gratuită · livrare până pe 23.12',
        'Envoltorio gratis · entrega antes del 23/12',
      ),
      { x: gt.x, y: gt.y + gt.h * 0.62, w: gt.w, h: gt.h * 0.22 },
      {
        fontFamily: 'Montserrat',
        fontWeight: 600,
        fontSize: b.s(0.036),
        color: C.green,
        align: al,
        lineHeight: 1.3,
      },
      { enter: step('rise', 900, 500, 'out-expo') },
    );

    const s2 = b.sc('offer');
    b.backdrop(s2, 'offer_bg', rad('#E8404F', C.red));
    const stars = [
      { x: 0.06, y: 0.08, s: 0.07 },
      { x: 0.84, y: 0.12, s: 0.05 },
      { x: 0.1, y: 0.82, s: 0.05 },
      { x: 0.86, y: 0.8, s: 0.08 },
    ];
    stars.forEach((st, i) =>
      b.shape(
        s2,
        `twinkle_${i + 1}`,
        { ...b.in(st.x, st.y, 0, 0), w: b.s(st.s), h: b.s(st.s) },
        { shape: 'star', points: 4, innerRatio: 0.3, fill: solid(C.gold) },
        {
          enter: step('pop', 200 + i * 150, 400, 'out-back'),
          emphasis: emph('pulse', 700 + i * 250, 1200, 0),
        },
      ),
    );
    b.text(
      s2,
      'discount',
      '−30%',
      b.in(0, 0.18, 1, 0.38),
      {
        fontFamily: 'Montserrat',
        fontWeight: 900,
        fontSize: b.s(0.28),
        color: C.cream,
        lineHeight: 1,
      },
      { enter: step('pop', 0, 550, 'spring-bouncy'), emphasis: emph('heartbeat', 1100, 900) },
    );
    b.text(
      s2,
      'discount_note',
      t(
        'on all gifts',
        'на всі подарунки',
        'на все подарки',
        'na wszystkie prezenty',
        'la toate cadourile',
        'en todos los regalos',
      ),
      b.in(0, 0.56, 1, 0.16),
      { ...script, fontSize: b.s(0.1), color: C.gold },
      { enter: step('fade', 500, 700) },
    );
    b.text(
      s2,
      'deadline',
      t(
        'until December 24',
        'до 24 грудня',
        'до 24 декабря',
        'do 24 grudnia',
        'până pe 24 decembrie',
        'hasta el 24 de diciembre',
      ),
      b.in(0, 0.76, 1, 0.08),
      caps(b.s(0.032), C.cream),
      { enter: step('rise', 900, 500, 'out-expo') },
    );
    b.addCamera(s2, 'punch', 0, 450, { intensity: 1 });

    const s3 = b.sc('cta');
    b.photo(
      s3,
      'photo_cta',
      'fireworks',
      b.full(),
      { brightness: 0.6 },
      { keys: b.kenBurns(1.04, 1.14) },
    );
    b.shape(s3, 'cta_tint', b.full(), { fill: solid('#0E3B2E80') });
    b.text(
      s3,
      'cta_headline',
      t(
        'Make it magical',
        'Створіть диво',
        'Создайте чудо',
        'Spraw magię',
        'Creează magie',
        'Haz magia',
      ),
      b.in(0, 0.2, 1, 0.3),
      { ...script, fontSize: b.s(0.16), color: C.cream },
      { enter: step('letters-blur', 0, 800), split: { by: 'char', stagger: 40 } },
    );
    b.chip(
      s3,
      'cta',
      t(
        'Shop gifts',
        'Обрати подарунки',
        'Выбрать подарки',
        'Wybierz prezenty',
        'Alege cadouri',
        'Elegir regalos',
      ),
      { x: b.cx, y: F.y + F.h * 0.58, h: b.s(0.11), align: 'center' },
      { bg: C.gold, color: C.green, font: 'Montserrat', weight: 800, upper: true },
      { enter: step('pop', 700, 450, 'out-back'), emphasis: emph('pulse', 1300, 1200, 0) },
    );
    b.text(s3, 'site', 'giftshop.com', b.in(0, 0.8, 1, 0.08), caps(b.s(0.03), C.cream), {
      enter: step('fade', 1000, 500),
    });
    b.addFx(s3, 'confetti', 300, 2400, {
      color: C.gold,
      color2: C.red,
      color3: C.cream,
      count: 70,
      y: 55,
    });
    return solid(C.green);
  },
};

/** Podcast promo: live waveform, a quote set word by word, player progress bar. */
const podcast: VideoTemplateDef = {
  id: 'podcast-quote',
  category: 'social',
  format: 'vertical',
  name: t(
    'Podcast quote',
    'Цитата з подкасту',
    'Цитата из подкаста',
    'Cytat z podcastu',
    'Citat din podcast',
    'Cita de pódcast',
  ),
  description: t(
    'Live waveform, word-by-word quote and a player progress bar',
    'Жива хвиля звуку, цитата слово за словом і прогрес плеєра',
    'Живая звуковая волна, цитата слово за словом и прогресс плеера',
    'Żywa fala dźwięku, cytat słowo po słowie i pasek postępu',
    'Undă audio vie, citat cuvânt cu cuvânt și bară de progres',
    'Onda de audio viva, cita palabra a palabra y barra de progreso',
  ),
  scenes: [
    {
      key: 'episode',
      name: t('Episode', 'Випуск', 'Выпуск', 'Odcinek', 'Episod', 'Episodio'),
      duration: 3200,
      transition: { preset: 'slide-over', duration: 500, params: { direction: 'up' } },
    },
    {
      key: 'quote',
      name: t('Quote', 'Цитата', 'Цитата', 'Cytat', 'Citat', 'Cita'),
      duration: 4200,
      transition: { preset: 'push', duration: 500, params: { direction: 'left' } },
    },
    {
      key: 'guest',
      name: t('Guest', 'Гість', 'Гость', 'Gość', 'Invitat', 'Invitado'),
      duration: 2800,
      transition: { preset: 'zoom-in', duration: 500 },
    },
    { key: 'cta', name: SN.cta, duration: 2600 },
  ],
  build(b) {
    const C = {
      charcoal: '#141418',
      orange: '#FF6B2C',
      off: '#F2EFEA',
      grey: '#8B8B95',
      panel: '#1F1F26',
    };
    const F = b.F;
    const head: Partial<TextLayer> = { fontFamily: 'Geologica', fontWeight: 800, lineHeight: 1.05 };
    const mono: Partial<TextLayer> = { fontFamily: 'JetBrains Mono', fontWeight: 500 };

    // Bars of an audio waveform that keep moving through a scene (deterministic "audio")
    const wave = (
      sc: ReturnType<typeof b.sc>,
      prefix: string,
      box: Box,
      n: number,
      colors: [string, string],
    ) => {
      const bw = box.w / n;
      for (let i = 0; i < n; i++) {
        const base = 0.35 + 0.65 * Math.abs(Math.sin(i * 1.7 + 0.4));
        const h = box.h * base;
        const keys: RelKey[] = [];
        for (let k = 0, at = 0; at < sc.duration - 50; k++, at += 260)
          keys.push({
            at,
            scale: 0.55 + 0.45 * Math.abs(Math.sin(i * 2.3 + k * 1.31)),
            easing: 'ease-in-out',
          });
        b.shape(
          sc,
          `${prefix}_${i + 1}`,
          { x: box.x + i * bw + bw * 0.2, y: box.y + (box.h - h) / 2, w: bw * 0.6, h },
          { fill: solid(i % 4 === 1 ? colors[0] : colors[1]), borderRadius: bw },
          { enter: step('unfold', 300 + i * 30, 400, 'out-back', { direction: 'up' }), keys },
        );
      }
    };

    const s0 = b.sc('episode');
    b.backdrop(s0, 'episode_bg', solid(C.charcoal));
    const cover = b.pick<Box>({
      tall: b.centered(b.cx, F.y + F.h * 0.3, F.w * 0.66, F.w * 0.66),
      portrait: b.centered(b.cx, F.y + F.h * 0.27, F.h * 0.46, F.h * 0.46),
      square: b.centered(F.x + F.h * 0.27, F.y + F.h * 0.42, F.h * 0.54, F.h * 0.54),
      wide: b.centered(F.x + F.h * 0.4, b.cy, F.h * 0.8, F.h * 0.8),
    });
    b.photo(
      s0,
      'cover_photo',
      'vr-headset',
      cover,
      { borderRadius: b.s(0.04) },
      { enter: step('pop', 0, 600, 'out-back'), keys: b.kenBurns(1, 1.05) },
    );
    b.chip(
      s0,
      'episode_tag',
      t('EP. 42', 'ВИП. 42', 'ВЫП. 42', 'ODC. 42', 'EP. 42', 'EP. 42'),
      { x: cover.x + b.s(0.025), y: cover.y + b.s(0.025), h: b.s(0.07) },
      { bg: C.orange, color: C.charcoal, font: 'JetBrains Mono', weight: 700 },
      { enter: step('pop', 400, 400, 'out-back') },
    );
    const et = b.upright
      ? {
          x: F.x,
          y: cover.y + cover.h + b.s(0.04),
          w: F.w,
          h: F.y + F.h - (cover.y + cover.h + b.s(0.04)),
        }
      : {
          x: cover.x + cover.w + b.s(0.06),
          y: cover.y,
          w: F.x + F.w - (cover.x + cover.w + b.s(0.06)),
          h: cover.h,
        };
    const al = b.upright ? 'center' : 'left';
    b.text(
      s0,
      'episode_title',
      t(
        'The future of work is weird',
        'Майбутнє роботи — дивне',
        'Будущее работы — странное',
        'Przyszłość pracy jest dziwna',
        'Viitorul muncii e ciudat',
        'El futuro del trabajo es raro',
      ),
      { x: et.x, y: et.y, w: et.w, h: et.h * 0.5 },
      { ...head, fontSize: b.s(0.085), color: C.off, align: al },
      { enter: step('words-rise', 500, 500), split: { by: 'word', stagger: 80 } },
    );
    wave(
      s0,
      'wave',
      {
        x: et.x + (b.upright ? et.w * 0.1 : 0),
        y: et.y + et.h * 0.58,
        w: et.w * (b.upright ? 0.8 : 0.9),
        h: et.h * 0.32,
      },
      18,
      [C.orange, C.grey],
    );

    const s1 = b.sc('quote');
    b.backdrop(s1, 'quote_bg', solid(C.off));
    b.text(
      s1,
      'quote_mark',
      '“',
      b.in(0, 0, 0.3, 0.19),
      {
        ...head,
        fontWeight: 900,
        fontSize: b.s(0.3),
        color: C.orange,
        align: 'left',
        verticalAlign: 'top',
        lineHeight: 1,
      },
      { enter: step('drop', 0, 600, 'bounce') },
    );
    b.text(
      s1,
      'quote',
      t(
        'Hire for curiosity. Skills are the easy part.',
        'Наймайте за допитливість. Навички — це найпростіше.',
        'Нанимайте за любопытство. Навыки — самое простое.',
        'Zatrudniaj ciekawość. Umiejętności to łatwa część.',
        'Angajează curiozitatea. Abilitățile sunt partea ușoară.',
        'Contrata la curiosidad. Las habilidades son lo fácil.',
      ),
      b.in(0, 0.2, 1, 0.5),
      { ...head, fontSize: b.s(0.095), color: C.charcoal, align: 'left', lineHeight: 1.12 },
      { enter: step('words-rise', 300, 450), split: { by: 'word', stagger: 140 } },
    );
    b.text(
      s1,
      'attribution',
      t(
        '— Daniel Park, founder of Loop',
        '— Деніел Парк, засновник Loop',
        '— Дэниел Парк, основатель Loop',
        '— Daniel Park, założyciel Loop',
        '— Daniel Park, fondatorul Loop',
        '— Daniel Park, fundador de Loop',
      ),
      b.in(0, 0.74, 1, 0.08),
      { ...mono, fontSize: b.s(0.036), color: '#55555E', align: 'left' },
      { enter: step('fade', 2200, 500) },
    );
    const track = b.in(0, 0.92, 1, 0);
    const th = Math.max(4, b.s(0.01));
    b.shape(
      s1,
      'progress_track',
      { x: track.x, y: track.y, w: track.w, h: th },
      { fill: solid('#14141826'), borderRadius: th },
    );
    b.shape(
      s1,
      'progress',
      { x: track.x, y: track.y, w: track.w, h: th },
      { fill: solid(C.orange), borderRadius: th },
      { enter: step('wipe', 200, 3700, 'linear', { direction: 'right' }) },
    );

    const s2 = b.sc('guest');
    b.backdrop(s2, 'guest_bg', solid(C.charcoal));
    const gd = b.pick({
      tall: F.w * 0.62,
      portrait: F.h * 0.42,
      square: F.h * 0.46,
      wide: F.h * 0.66,
    });
    const gc = b.upright
      ? { x: b.cx, y: F.y + F.h * 0.27 }
      : { x: F.x + F.w * (b.wide ? 0.27 : 0.27), y: b.cy };
    b.shape(
      s2,
      'guest_ring',
      b.centered(gc.x, gc.y, gd * 1.1, gd * 1.1),
      { shape: 'ellipse', fill: solid(C.orange) },
      {
        enter: step('zoom-in', 0, 600),
      },
    );
    b.photo(
      s2,
      'guest_photo',
      'remote-work',
      b.centered(gc.x, gc.y, gd, gd),
      { borderRadius: gd, focusX: 0.55 },
      {
        enter: step('pop', 100, 550, 'out-back'),
      },
    );
    const mic = b.s(0.1);
    b.shape(
      s2,
      'mic_badge',
      b.centered(gc.x + gd * 0.38, gc.y + gd * 0.38, mic * 1.4, mic * 1.4),
      { shape: 'ellipse', fill: solid(C.off) },
      {
        enter: step('pop', 500, 400, 'spring-bouncy'),
      },
    );
    b.icon(
      s2,
      'mic_icon',
      'mic',
      b.centered(gc.x + gd * 0.38, gc.y + gd * 0.38, mic * 0.75, mic * 0.75),
      C.charcoal,
      {
        enter: step('pop', 550, 400, 'spring-bouncy'),
        emphasis: emph('wiggle', 1200, 700),
      },
    );
    const gt = b.upright
      ? b.in(0, 0.58, 1, 0.42)
      : b.in(b.wide ? 0.54 : 0.56, 0.2, b.wide ? 0.46 : 0.44, 0.6);
    const gal = b.upright ? 'center' : 'left';
    b.text(
      s2,
      'guest_name',
      'Daniel Park',
      { x: gt.x, y: gt.y, w: gt.w, h: gt.h * 0.36 },
      { ...head, fontSize: b.s(0.1), color: C.off, align: gal },
      { enter: step('rise', 300, 500, 'out-expo') },
    );
    b.text(
      s2,
      'guest_role',
      t(
        'Founder of Loop · ex-product lead',
        'Засновник Loop · ексголова продукту',
        'Основатель Loop · экс-руководитель продукта',
        'Założyciel Loop · ex-szef produktu',
        'Fondatorul Loop · fost lider de produs',
        'Fundador de Loop · ex jefe de producto',
      ),
      { x: gt.x, y: gt.y + gt.h * 0.38, w: gt.w, h: gt.h * 0.2 },
      { ...mono, fontSize: b.s(0.034), color: C.grey, align: gal },
      { enter: step('fade', 600, 400) },
    );
    b.chip(
      s2,
      'duration',
      t(
        'Full episode · 48 min',
        'Повний випуск · 48 хв',
        'Полный выпуск · 48 мин',
        'Cały odcinek · 48 min',
        'Episod complet · 48 min',
        'Episodio completo · 48 min',
      ),
      {
        x: gal === 'center' ? gt.x + gt.w / 2 : gt.x,
        y: gt.y + gt.h * 0.66,
        h: b.s(0.075),
        align: gal,
      },
      { bg: C.panel, color: C.orange, font: 'JetBrains Mono', weight: 600, k: 0.62 },
      { enter: step('wipe', 900, 500, 'out-expo', { direction: 'right' }) },
    );

    const s3 = b.sc('cta');
    b.backdrop(s3, 'cta_bg', solid(C.orange));
    const pl = b.s(0.18);
    b.shape(
      s3,
      'play_button',
      b.centered(b.cx, F.y + F.h * (b.tall ? 0.2 : 0.18), pl, pl),
      { shape: 'ellipse', fill: solid(C.charcoal) },
      {
        enter: step('pop', 0, 500, 'spring-bouncy'),
        emphasis: emph('heartbeat', 800, 900, 0, 400),
      },
    );
    b.icon(
      s3,
      'play_icon',
      'play',
      b.centered(b.cx + pl * 0.04, F.y + F.h * (b.tall ? 0.2 : 0.18), pl * 0.45, pl * 0.45),
      C.orange,
      {
        enter: step('pop', 100, 500, 'spring-bouncy'),
        emphasis: emph('heartbeat', 800, 900, 0, 400),
      },
    );
    b.text(
      s3,
      'cta_headline',
      t(
        'Hear the full talk',
        'Слухай повну розмову',
        'Слушай полный разговор',
        'Posłuchaj całej rozmowy',
        'Ascultă discuția completă',
        'Escucha la charla completa',
      ),
      b.in(0, b.tall ? 0.34 : 0.33, 1, 0.24),
      { ...head, fontSize: b.s(0.1), color: C.charcoal },
      { enter: step('words-rise', 200, 500), split: { by: 'word', stagger: 80 } },
    );
    b.chip(
      s3,
      'cta',
      t('Listen now', 'Слухати', 'Слушать', 'Słuchaj', 'Ascultă acum', 'Escuchar'),
      { x: b.cx, y: F.y + F.h * 0.62, h: b.s(0.11), align: 'center' },
      { bg: C.charcoal, color: C.off, font: 'Geologica', weight: 800 },
      { enter: step('pop', 700, 450, 'out-back') },
    );
    b.text(
      s3,
      'site',
      t(
        'New episodes every Tuesday · loopcast.fm',
        'Нові випуски щовівторка · loopcast.fm',
        'Новые выпуски по вторникам · loopcast.fm',
        'Nowe odcinki we wtorki · loopcast.fm',
        'Episoade noi marțea · loopcast.fm',
        'Episodios nuevos cada martes · loopcast.fm',
      ),
      b.in(0, 0.8, 1, 0.08),
      { ...mono, fontSize: b.s(0.032), color: C.charcoal },
      { enter: step('fade', 1000, 400) },
    );
    return solid(C.charcoal);
  },
};

export const TEMPLATES_C: VideoTemplateDef[] = [beautySoft, autoOffer, hiring, holiday, podcast];
